import {
  MaximumTableHeight,
  MaximumTableHeightType,
  TABLE_AXIS_CELL_HEIGHT,
} from 'notebook/tables/constants/TableLayoutConstants';
import {
  computeCurrentMaxTableHeight,
  computeFormulaBarHeight,
  computeIndentationPx,
  computeMaxTableHeight,
  computeMinTableHeight,
  computePaddingPx,
  computePrintableLayoutWidth,
  computeStickyTopContentHeight,
  computeTitleBarHeight,
} from 'notebook/tables/store/logic/TableMeasurementsLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

import {TableDimensionsModel} from './TableDimensionsSelectors';
import {TableViewsModel} from './TableViewsSelectors';

/**
 * Model containing prop/state-derived table measurements.
 *
 * DOM-dependent measurements (formula box height, current table height) are not
 * measured live here; instead their measured inputs are captured on
 * `TableEditableState` (via a `ResizeObserver` and the periodic table
 * measurement pass) and the pure math is applied in this selector. This keeps
 * the values off the render hot path so they don't force a layout reflow on
 * every render.
 */
export interface TableMeasurementsModel {
  /**
   * The numeric maximum height for the table, ignoring whether the table is
   * maximized or intended to stretch to full height.
   */
  maxTableHeight: number;

  /**
   * The minimum height that the `<table>` tag must be, using the default column
   * header height. Call sites that have measured a taller header row should
   * recompute against that measured height instead.
   */
  minTableHeight: number;

  /**
   * The maximum height of the table when it is fixed-height, or `undefined` when
   * the table shows every row (maximized/split screen, full height, or viewer).
   */
  currentMaxTableHeight: number | undefined;

  /**
   * The current rendered height of the table: `null` when the table has no fixed
   * height, `MaximumTableHeight.FULL` when it expands to show all rows, `0` when
   * collapsed, or a numeric pixel height. Derived from the DOM-measured row
   * heights captured on state.
   */
  currentTableHeight: MaximumTableHeightType | null;

  /**
   * The height of the title bar, when visible (0 when the title bar is
   * suppressed).
   */
  titleBarHeight: number;

  /**
   * The height of the formula box, when visible (0 when the formula box is
   * hidden). Uses the DOM-measured height captured on state, falling back to the
   * inline constant before the first measurement.
   */
  formulaBarHeight: number;

  /**
   * The height of the sticky content above the sticky column-header row (title
   * bar + formula box).
   */
  stickyTopContentHeight: number;

  /** The left indentation (in px) applied to the table. */
  indentationPx: number;

  /** The right padding (in px) applied to the table. */
  paddingPx: number;

  /** The width (in px) of the printable layout container. */
  printableLayoutWidth: number;

  /**
   * The width (in px) of the leading axis column, falling back to the default column width when
   * the table does not set one. Zero when the table renders without an axis column.
   */
  firstColumnWidth: number;

  /** The width (in px) of each of the table's data columns. */
  columnWidths: Array<number>;
}

/**
 * Creates a memoized selector that computes the TableMeasurementsModel from the store.
 */
export function createTableMeasurementsSelector(
  tableViewsSelector: (store: TableEditableStore) => TableViewsModel,
  tableDimensionsSelector: (store: TableEditableStore) => TableDimensionsModel,
  boundaryIndexSelector: (store: TableEditableStore) => number | null
): (store: TableEditableStore) => TableMeasurementsModel {
  return createTableStoreSelector(
    'tableMeasurements',
    {
      maximumHeight: (store) => store.stableProps.maximumHeight,
      rowCount: (store) => store.volatileProps.table.table.length,
      widths: (store) => store.volatileProps.table.widths,
      firstColumnWidth: (store) => TableUtil.firstColumnWidth(store.volatileProps.table),
      isFullHeight: (store) => tableViewsSelector(store).isFullHeight,
      isMaximizedOrSplitScreen: (store) => tableViewsSelector(store).isMaximizedOrSplitScreen,
      suppressTitleBar: (store) => store.stableProps.suppressTitleBar ?? false,
      indentation: (store) => store.stableProps.indentation ?? 0,
      documentName: (store) => store.stableProps.documentName,
      viewer: (store) => store.stableProps.viewer ?? false,
      showFormulaBox: (store) => store.stableProps.showFormulaBox,
      formulaBoxHeight: (store) => store.state.formulaBoxHeight,
      currentFixedTableHeight: (store) => store.state.currentFixedTableHeight,
      shouldShowGhostCells: (store) => tableDimensionsSelector(store).shouldShowGhostCells,
      boundaryIndex: boundaryIndexSelector,
    },
    ({
      maximumHeight,
      rowCount,
      widths,
      firstColumnWidth,
      isFullHeight,
      isMaximizedOrSplitScreen,
      suppressTitleBar,
      indentation,
      documentName,
      viewer,
      showFormulaBox,
      formulaBoxHeight,
      currentFixedTableHeight,
      shouldShowGhostCells,
      boundaryIndex,
    }) => {
      const maxTableHeight = computeMaxTableHeight(maximumHeight);
      const titleBarHeight = computeTitleBarHeight(suppressTitleBar);
      const indentationPx = computeIndentationPx(isMaximizedOrSplitScreen, indentation);
      const formulaBarHeight = computeFormulaBarHeight(showFormulaBox, formulaBoxHeight ?? undefined);
      // `widths` is always present on a real table; guard against partially-constructed tables
      // (e.g. in unit-test mocks) so building the model never throws.
      const tableWidth = widths
        ? TableUtil.tableWidth(widths, shouldShowGhostCells, {
            sliceStart: boundaryIndex || null,
            sliceEnd: null,
            firstColumnWidth,
          })
        : 0;
      return {
        maxTableHeight,
        minTableHeight: computeMinTableHeight(rowCount, TABLE_AXIS_CELL_HEIGHT, isFullHeight, maxTableHeight),
        currentMaxTableHeight: computeCurrentMaxTableHeight({
          hasFullMaximumHeight: maximumHeight === MaximumTableHeight.FULL,
          isMaximizedOrSplitScreen,
          isFullHeight,
          viewer,
          maxTableHeight,
        }),
        currentTableHeight: currentFixedTableHeight,
        titleBarHeight,
        formulaBarHeight,
        stickyTopContentHeight: computeStickyTopContentHeight(titleBarHeight, formulaBarHeight),
        indentationPx,
        paddingPx: computePaddingPx(isMaximizedOrSplitScreen, Boolean(documentName)),
        printableLayoutWidth: computePrintableLayoutWidth(indentationPx, tableWidth),
        firstColumnWidth,
        columnWidths: widths,
      };
    }
  );
}
